/**
 * Holographic hover effect with smooth lenticular image blend.
 * Uses requestAnimationFrame + lerp for buttery smooth movement.
 *
 * @param {string} cardSelector
 * @param {Object} options
 * @param {number} options.tiltStrength - Max tilt degrees (default: 20)
 * @param {number} options.ridges - Lenticular wave count (default: 4)
 * @param {number} options.smoothing - Lerp factor 0-1, lower = smoother (default: 0.08)
 */
export function initHoloEffect(cardSelector = "#card", options = {}) {
  const { tiltStrength = 20, ridges = 4, smoothing = 0.3 } = options;

  const card = document.querySelector(cardSelector);

  if (!card) {
    console.warn(`[holoEffect] Card not found: "${cardSelector}"`);
    return null;
  }

  const imgFront = card.querySelector(".img-front");
  const imgBack = card.querySelector(".img-back");
  const shine = card.querySelector(".shine");

  if (imgFront) imgFront.style.transition = "opacity 0.3s ease";
  if (imgBack) imgBack.style.transition = "opacity 0.3s ease";

  let target = { x: 0.5, y: 0.5 };
  let current = { x: 0.5, y: 0.5 };
  let hovering = false;
  let gyroActive = false;
  let rafId = null;

  const lerp = (a, b, t) => a + (b - a) * t;

  const tick = () => {
    current.x = lerp(current.x, target.x, smoothing);
    current.y = lerp(current.y, target.y, smoothing);

    const nx = (current.x - 0.5) * 2;
    const ny = (0.5 - current.y) * 2;

    card.style.transform = `rotateX(${ny * tiltStrength}deg) rotateY(${nx * tiltStrength}deg)`;

    const angle =
      Math.atan2(current.y - 0.5, current.x - 0.5) * (180 / Math.PI) + 180;
    card.style.setProperty("--holo-angle", angle + "deg");
    card.style.setProperty("--shine-angle", angle + "deg");

    if (shine) {
      shine.style.backgroundPosition = `${current.x * 100}% ${current.y * 100}%`;
    }

    const wave = Math.sin(current.x * ridges * Math.PI);
    const blend = (wave + 1) / 2;

    if (imgFront) imgFront.style.opacity = 1 - blend;
    if (imgBack) imgBack.style.opacity = blend;

    if (hovering || gyroActive) rafId = requestAnimationFrame(tick);
  };

  // ─── Mouse (desktop) ───────────────────────────────────────────────────────

  const onMouseMove = (e) => {
    const rect = card.getBoundingClientRect();
    target.x = (e.clientX - rect.left) / rect.width;
    target.y = (e.clientY - rect.top) / rect.height;
  };

  const onMouseEnter = () => {
    hovering = true;
    card.style.transition = "none";
    rafId = requestAnimationFrame(tick);
  };

  const onMouseLeave = () => {
    hovering = false;
    if (rafId) cancelAnimationFrame(rafId);
    card.style.transition = "transform 0.5s ease";
    card.style.transform = "rotateX(0) rotateY(0)";
    if (imgFront) imgFront.style.opacity = "1";
    if (imgBack) imgBack.style.opacity = "0";
  };

  // ─── Giroscopio (móvil) ────────────────────────────────────────────────────

  // Rango de grados del giroscopio que mapeas a 0-1
  const GYRO_RANGE = 30;

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
  const mapGyro = (v) => clamp((v + GYRO_RANGE) / (GYRO_RANGE * 2), 0, 1);

  const onDeviceOrientation = (e) => {
    // gamma = inclinación izquierda/derecha (-90 a 90)
    // beta  = inclinación adelante/atrás   (-180 a 180)
    const gamma = e.gamma ?? 0; // eje X → rotateY
    const beta = e.beta ?? 0; // eje Y → rotateX

    target.x = mapGyro(gamma);
    target.y = mapGyro(beta - 30); // -30 para compensar el ángulo natural de sujeción
  };

  const initGyro = () => {
    if (typeof DeviceOrientationEvent?.requestPermission === "function") {
      DeviceOrientationEvent.requestPermission()
        .then((state) => {
          if (state === "granted") {
            window.addEventListener("deviceorientation", onDeviceOrientation);
            gyroActive = true;
            rafId = requestAnimationFrame(tick);
          }
        })
        .catch(console.error);
    } else {
      window.addEventListener("deviceorientation", onDeviceOrientation);
      gyroActive = true;
      rafId = requestAnimationFrame(tick);
    }
  };
  // Detectar si es móvil/tablet
  const isMobile = () => window.matchMedia("(pointer: coarse)").matches;

  if (isMobile()) {
    if (typeof DeviceOrientationEvent?.requestPermission === "function") {
      document.addEventListener("touchstart", initGyro, { once: true });
    } else {
      initGyro();
    }
  } else {
    // ← esto te falta!
    card.addEventListener("mousemove", onMouseMove);
    card.addEventListener("mouseenter", onMouseEnter);
    card.addEventListener("mouseleave", onMouseLeave);
  }

  return {
    destroy() {
      hovering = false;
      gyroActive = false;
      if (rafId) cancelAnimationFrame(rafId);
      card.removeEventListener("mousemove", onMouseMove);
      card.removeEventListener("mouseenter", onMouseEnter);
      card.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("deviceorientation", onDeviceOrientation);
      if (imgFront) imgFront.style.opacity = "1";
      if (imgBack) imgBack.style.opacity = "0";
    },
  };
}
